import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { accountSummaryService } from '../../services/accountSummary.service';
import { useSelector } from 'react-redux';
import { selectToken } from '../../redux/authSlice';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import SelectInput from '../../common_components/SelectInput';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'; // Meta
import GoogleIcon from '@mui/icons-material/Google'; // Placeholder for Google
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'; // Shopify
// Helper to format currency
const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(val);
};
// Helper to format percentage
const formatPercent = (val) => {
    return `${val.toFixed(2)}%`;
};
const PLATFORMS = [
    { key: 'meta', label: 'Meta', icon: _jsx(AllInclusiveIcon, { sx: { color: '#1877F2', fontSize: 18 } }) },
    { key: 'adword', label: 'Google', icon: _jsx(GoogleIcon, { sx: { color: '#EA4335', fontSize: 18 } }) }, // Use a proper Google Icon if available or colored generic
    { key: 'shopify', label: 'Shopify', icon: _jsx(ShoppingBagIcon, { sx: { color: '#95BF47', fontSize: 18 } }) },
];
const ProTeamLeaguePerformance = () => {
    const [searchParams] = useSearchParams();
    const token = useSelector(selectToken);
    const clientId = searchParams.get('clientId') || '';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedLeague, setSelectedLeague] = useState('All');
    const [entities, setEntities] = useState([]);
    const fetchData = async () => {
        if (!token)
            return;
        setLoading(true);
        try {
            const leagueParam = selectedLeague === 'All' ? undefined : selectedLeague;
            const [reportRes, entitiesRes] = await Promise.all([
                accountSummaryService.getTeamPerformanceReport(clientId, { league: leagueParam }, token),
                accountSummaryService.getPerformanceEntities({ type: 'group', client_id: clientId }, token)
            ]);
            if (reportRes && reportRes.data) {
                setData(reportRes.data);
            }
            else {
                console.error("Invalid response format:", reportRes);
            }
            if (entitiesRes && entitiesRes.data) {
                setEntities(entitiesRes.data);
            }
        }
        catch (error) {
            console.error("Error fetching data:", error);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [clientId, token, selectedLeague]);
    // Extract unique leagues from fetch entities
    const leagueOptions = useMemo(() => {
        const opts = entities.map(e => ({ label: e.group_name, value: e.group_name }));
        return [{ label: 'All', value: 'All' }, ...opts.sort((a, b) => a.label.localeCompare(b.label))];
    }, [entities]);
    // Header row heights for sticky positioning
    const HEADER_ROW_1_HEIGHT = 50; // First header row (date ranges)
    const HEADER_ROW_2_HEIGHT = 40; // Second header row (metrics)
    // Common Header Cell Style
    const rangeHeaderStyle = (bgColor) => ({
        bgcolor: bgColor,
        color: 'white',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '12px',
        borderRight: '1px solid rgba(255,255,255,0.2)',
        textAlign: 'center',
        py: 1.5
    });
    const subHeaderStyle = {
        bgcolor: '#f8fafc',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase',
        py: 1,
        px: 1,
        borderBottom: '1px solid #e2e8f0',
        whiteSpace: 'nowrap',
        textAlign: 'right'
    };
    const stickyColStyle = {
        position: 'sticky',
        left: 0,
        bgcolor: 'white',
        zIndex: 10,
        borderRight: '1px solid #e2e8f0'
    };
    // Row Height for consistent sticky positioning
    const ROW_HEIGHT = 45;
    // Sort rows: Regular teams first, Total last.
    const { teamRows, totalRow } = useMemo(() => {
        if (!data?.data?.rows)
            return { teamRows: [], totalRow: null };
        const rows = data.data.rows;
        const total = rows.find(r => r.is_total);
        const teams = rows.filter(r => !r.is_total).sort((a, b) => a.team.localeCompare(b.team));
        return { teamRows: teams, totalRow: total };
    }, [data]);
    if (!token)
        return null;
    return (_jsxs(Box, { sx: { p: 2, height: '90vh', display: 'flex', flexDirection: 'column' }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }, children: [_jsx(Box, { children: _jsx(Typography, { variant: "h5", sx: { fontWeight: 700, color: '#0f172a' }, children: "League/Team Performance" }) }), _jsx(Box, { sx: { width: { xs: '100%', sm: 200 }, minWidth: 160 }, children: _jsx(SelectInput, { label: "", value: selectedLeague, onChange: (e) => setSelectedLeague(e.target.value), options: leagueOptions, placeholder: "Select League" }) })] }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 10 }, children: _jsx(CircularProgress, {}) })) : !data ? (_jsx(Box, { sx: { p: 4, textAlign: 'center' }, children: _jsx(Typography, { color: "text.secondary", children: "No data available." }) })) : (_jsx(TableContainer, { component: Paper, sx: {
                    flex: 1,
                    overflow: 'auto',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                }, children: _jsxs(Table, { stickyHeader: true, size: "small", children: [_jsxs(TableHead, { children: [_jsxs(TableRow, { sx: {
                                        height: `${HEADER_ROW_1_HEIGHT}px`
                                    }, children: [_jsx(TableCell, { rowSpan: 2, sx: {
                                                ...stickyColStyle,
                                                bgcolor: '#f1f5f9',
                                                color: '#475569',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                textTransform: 'uppercase',
                                                minWidth: 200,
                                                zIndex: 13, // Higher than date range headers
                                                position: 'sticky',
                                                top: 0,
                                                left: 0
                                            }, children: "Team" }), _jsx(TableCell, { rowSpan: 2, sx: {
                                                width: 40,
                                                bgcolor: '#f1f5f9',
                                                borderRight: '1px solid #e2e8f0',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 12
                                            } }), _jsxs(TableCell, { colSpan: 5, sx: {
                                                ...rangeHeaderStyle('#0f172a'),
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 12
                                            }, children: ["Yesterday (", data?.ranges?.yesterday?.start_date, ")"] }), _jsxs(TableCell, { colSpan: 5, sx: {
                                                ...rangeHeaderStyle('#581c87'),
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 12
                                            }, children: ["L7D (", data?.ranges?.['7d']?.start_date, " - ", data?.ranges?.['7d']?.end_date, ")"] }), _jsxs(TableCell, { colSpan: 5, sx: {
                                                ...rangeHeaderStyle('#7f1d1d'),
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 12
                                            }, children: ["L30D (", data?.ranges?.['30d']?.start_date, " - ", data?.ranges?.['30d']?.end_date, ")"] })] }), _jsxs(TableRow, { sx: {
                                        height: `${HEADER_ROW_2_HEIGHT}px`
                                    }, children: [['Revenue', '% of TTL', 'Spend', '% of TTL', 'ROAS'].map((h, i) => (_jsx(TableCell, { sx: {
                                                ...subHeaderStyle,
                                                position: 'sticky',
                                                top: `${HEADER_ROW_1_HEIGHT}px`,
                                                zIndex: 12
                                            }, children: h }, `y-${i}`))), ['Revenue', '% of TTL', 'Spend', '% of TTL', 'ROAS'].map((h, i) => (_jsx(TableCell, { sx: {
                                                ...subHeaderStyle,
                                                position: 'sticky',
                                                top: `${HEADER_ROW_1_HEIGHT}px`,
                                                zIndex: 12
                                            }, children: h }, `7d-${i}`))), ['Revenue', '% of TTL', 'Spend', '% of TTL', 'ROAS'].map((h, i) => (_jsx(TableCell, { sx: {
                                                ...subHeaderStyle,
                                                position: 'sticky',
                                                top: `${HEADER_ROW_1_HEIGHT}px`,
                                                zIndex: 12
                                            }, children: h }, `30d-${i}`)))] })] }), _jsxs(TableBody, { children: [teamRows.map((row, rowIdx) => (_jsx(React.Fragment, { children: PLATFORMS.map((platform, pIdx) => (_jsxs(TableRow, { sx: {
                                            height: `${ROW_HEIGHT}px`,
                                            '&:hover': { bgcolor: '#f8fafc' },
                                            borderBottom: pIdx === 2 ? '2px solid #cbd5e1' : 'none'
                                        }, children: [pIdx === 0 && (_jsx(TableCell, { rowSpan: 3, sx: {
                                                    ...stickyColStyle,
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: '#1e293b',
                                                    verticalAlign: 'middle',
                                                    zIndex: 1,
                                                    p: 1.5
                                                }, children: row.team })), _jsx(TableCell, { sx: {
                                                    textAlign: 'center',
                                                    py: 0.5,
                                                    borderRight: '1px solid #f1f5f9',
                                                    borderBottom: pIdx === 2 ? '2px solid #e2e8f0' : 'none'
                                                }, children: platform.icon }), ['yesterday', '7d', '30d'].map((rangeKey) => {
                                                const metrics = row?.ranges?.[rangeKey]?.[platform.key];
                                                // Shared Style for cells
                                                const cellStyle = {
                                                    textAlign: 'right',
                                                    fontSize: '14px',
                                                    color: '#334155',
                                                    py: 0.5,
                                                    px: 1,
                                                    fontWeight: 500,
                                                    borderBottom: pIdx === 2 ? '2px solid #e2e8f0' : 'none',
                                                    borderRight: '1px solid #f1f5f9',
                                                    height: `${ROW_HEIGHT}px`,
                                                    boxSizing: 'border-box'
                                                };
                                                return (_jsxs(React.Fragment, { children: [_jsx(TableCell, { sx: { ...cellStyle, fontWeight: 600 }, children: metrics?.revenue !== undefined ? formatCurrency(metrics.revenue) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.revenue_share !== undefined ? formatPercent(metrics.revenue_share) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.spend !== undefined ? formatCurrency(metrics.spend) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.spend_share !== undefined ? formatPercent(metrics.spend_share) : '-' }), _jsx(TableCell, { sx: { ...cellStyle, fontWeight: 700 }, children: metrics?.roas !== undefined ? metrics.roas.toFixed(2) : '-' })] }, `${row.team}-${platform.key}-${rangeKey}`));
                                            })] }, `${row.team}-${platform.key}`))) }, `${row.team}-${rowIdx}`))), totalRow && PLATFORMS.map((platform, pIdx) => {
                                    // Calculate bottom position for stacking
                                    const ROW_HEIGHT_PX = 45;
                                    const bottomOffset = (2 - pIdx) * ROW_HEIGHT_PX;
                                    return (_jsxs(TableRow, { sx: {
                                            height: `${ROW_HEIGHT_PX}px`,
                                            position: 'sticky',
                                            bottom: `${bottomOffset}px`,
                                            zIndex: 20,
                                            bgcolor: '#fffbf0',
                                            // Stronger shadow on the top row of the group
                                            boxShadow: pIdx === 0 ? '0px -4px 10px rgba(0,0,0,0.15)' : 'none',
                                            '& td': {
                                                bgcolor: '#fffbf0',
                                                borderRight: '1px solid #f1f5f9'
                                            }
                                        }, children: [pIdx === 0 && (_jsx(TableCell, { rowSpan: 3, sx: {
                                                    position: 'sticky',
                                                    left: 0,
                                                    bottom: `${2 * ROW_HEIGHT_PX}px`,
                                                    zIndex: 30, // Higher than other sticky cells
                                                    fontWeight: 800,
                                                    fontSize: '14px',
                                                    color: '#0f172a',
                                                    verticalAlign: 'middle',
                                                    bgcolor: '#fffbf0 !important',
                                                    borderTop: '2px solid #cbd5e1', // Distinct top border
                                                    borderRight: '1px solid #e2e8f0',
                                                    boxShadow: '0px -4px 10px rgba(0,0,0,0.15)', // Shadow for the corner cell too
                                                    p: 1.5
                                                }, children: totalRow.team })), _jsx(TableCell, { sx: {
                                                    position: 'sticky',
                                                    bottom: `${bottomOffset}px`,
                                                    textAlign: 'center',
                                                    py: 0.5,
                                                    bgcolor: '#fffbf0 !important',
                                                    zIndex: 20,
                                                    borderTop: pIdx === 0 ? '2px solid #cbd5e1' : 'none',
                                                    borderRight: '1px solid #f1f5f9',
                                                    height: `${ROW_HEIGHT_PX}px`,
                                                    boxSizing: 'border-box'
                                                }, children: platform.icon }), ['yesterday', '7d', '30d'].map((rangeKey) => {
                                                const metrics = totalRow?.ranges?.[rangeKey]?.[platform.key];
                                                const cellStyle = {
                                                    position: 'sticky',
                                                    bottom: `${bottomOffset}px`,
                                                    textAlign: 'right',
                                                    fontSize: '14px',
                                                    color: '#334155',
                                                    py: 0.5,
                                                    px: 1,
                                                    fontWeight: 500,
                                                    bgcolor: '#fffbf0 !important',
                                                    zIndex: 20,
                                                    borderRight: '1px solid #f1f5f9',
                                                    borderTop: pIdx === 0 ? '2px solid #cbd5e1' : 'none',
                                                    height: `${ROW_HEIGHT_PX}px`,
                                                    boxSizing: 'border-box'
                                                };
                                                return (_jsxs(React.Fragment, { children: [_jsx(TableCell, { sx: { ...cellStyle, fontWeight: 700 }, children: metrics?.revenue !== undefined ? formatCurrency(metrics.revenue) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.revenue_share !== undefined ? formatPercent(metrics.revenue_share) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.spend !== undefined ? formatCurrency(metrics.spend) : '-' }), _jsx(TableCell, { sx: cellStyle, children: metrics?.spend_share !== undefined ? formatPercent(metrics.spend_share) : '-' }), _jsx(TableCell, { sx: { ...cellStyle, fontWeight: 800 }, children: metrics?.roas !== undefined ? metrics.roas.toFixed(2) : '-' })] }, `total-${platform.key}-${rangeKey}`));
                                            })] }, `total-${platform.key}`));
                                })] })] }) }))] }));
};
export default ProTeamLeaguePerformance;
